/**
 * useDashboard — API hook for dashboard CRUD operations.
 * Uses useDashboardStore.getState() inside callbacks to avoid
 * stale closures and infinite re-render loops.
 */

import { useCallback } from 'react'
import { apiClient } from '@/services/apiClient'
import { useDashboardStore } from '@/stores/dashboardStore'
import type { Dashboard, DashboardSummary } from '@/types/dashboard'

const getState = () => useDashboardStore.getState()

export function useDashboard() {
  // State reads via individual selectors — only re-render when selected value changes
  const currentDashboard = useDashboardStore((s) => s.currentDashboard)
  const dashboards = useDashboardStore((s) => s.dashboards)
  const isEditMode = useDashboardStore((s) => s.isEditMode)
  const isLoading = useDashboardStore((s) => s.isLoading)
  const isDirty = useDashboardStore((s) => s.isDirty)

  const fetchDashboards = useCallback(async (): Promise<DashboardSummary[]> => {
    const res = await apiClient.fetch('/api/marketing/dashboards')
    if (!res.ok) throw new Error('Failed to fetch dashboards')
    const data = await res.json()
    // Transform snake_case to camelCase
    const items: DashboardSummary[] = data.map((d: any) => ({
      id: d.id,
      name: d.name,
      description: d.description,
      templateId: d.template_id,
      widgetCount: d.widget_count,
      createdAt: d.created_at,
      updatedAt: d.updated_at,
    }))
    getState().setDashboards(items)
    return items
  }, [])

  const fetchDashboard = useCallback(async (id: string): Promise<Dashboard> => {
    const res = await apiClient.fetch(`/api/marketing/dashboards/${id}`)
    if (!res.ok) throw new Error('Failed to fetch dashboard')
    const d = await res.json()
    const dashboard: Dashboard = {
      id: d.id,
      name: d.name,
      description: d.description,
      templateId: d.template_id,
      widgets: d.widgets || [],
      globalDateRange: d.global_date_range,
      columns: d.columns,
      rowHeight: d.row_height,
      createdAt: d.created_at,
      updatedAt: d.updated_at,
    }
    getState().setCurrentDashboard(dashboard)
    return dashboard
  }, [])

  const createDashboard = useCallback(async (params: {
    name: string
    description?: string
    templateId?: string
    widgets?: any[]
    globalDateRange?: number
  }): Promise<Dashboard> => {
    const res = await apiClient.fetch('/api/marketing/dashboards', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: params.name,
        description: params.description,
        template_id: params.templateId,
        widgets: params.widgets || [],
        global_date_range: params.globalDateRange ?? 30,
      }),
    })
    if (!res.ok) throw new Error('Failed to create dashboard')
    const d = await res.json()
    const dashboard: Dashboard = {
      id: d.id,
      name: d.name,
      description: d.description,
      templateId: d.template_id,
      widgets: d.widgets || [],
      globalDateRange: d.global_date_range,
      columns: d.columns,
      rowHeight: d.row_height,
      createdAt: d.created_at,
      updatedAt: d.updated_at,
    }
    getState().setCurrentDashboard(dashboard)
    return dashboard
  }, [])

  const saveDashboard = useCallback(async (): Promise<void> => {
    const current = getState().currentDashboard
    if (!current) return

    const res = await apiClient.fetch(`/api/marketing/dashboards/${current.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: current.name,
        description: current.description,
        widgets: current.widgets,
        global_date_range: current.globalDateRange,
      }),
    })
    if (!res.ok) throw new Error('Failed to save dashboard')
    getState().markClean()
  }, [])

  const deleteDashboard = useCallback(async (id: string): Promise<void> => {
    const res = await apiClient.fetch(`/api/marketing/dashboards/${id}`, {
      method: 'DELETE',
    })
    if (!res.ok) throw new Error('Failed to delete dashboard')
    // Refresh the list
    const listRes = await apiClient.fetch('/api/marketing/dashboards')
    if (listRes.ok) {
      const data = await listRes.json()
      const items: DashboardSummary[] = data.map((d: any) => ({
        id: d.id,
        name: d.name,
        description: d.description,
        templateId: d.template_id,
        widgetCount: d.widget_count,
        createdAt: d.created_at,
        updatedAt: d.updated_at,
      }))
      getState().setDashboards(items)
    }
  }, [])

  return {
    // State
    currentDashboard,
    dashboards,
    isEditMode,
    isLoading,
    isDirty,

    // API operations
    fetchDashboards,
    fetchDashboard,
    createDashboard,
    saveDashboard,
    deleteDashboard,

    // Store operations (re-exported for convenience — these are stable refs)
    setEditMode: getState().setEditMode,
    addWidget: getState().addWidget,
    removeWidget: getState().removeWidget,
    updateWidgetConfig: getState().updateWidgetConfig,
    updateWidgetTitle: getState().updateWidgetTitle,
    updateLayouts: getState().updateLayouts,
    updateGlobalDateRange: getState().updateGlobalDateRange,
    updateDashboardName: getState().updateDashboardName,
  }
}
